import { useState } from 'react';

import {
  useCampaignRecipients,
  useOutreachSettings,
} from '../../hooks/useOutreach';
import type { Campaign } from '../../types/outreach';
import styles from '../pages/OutreachPage.module.css';


/**
 * Modal that gates the Start button on non-draft / paused campaigns.
 *
 * Why: Brad's biggest pre-deploy concern was "what if I forget global
 * test mode is off and start a real campaign by mistake?" The persistent
 * banner catches it visually but doesn't force a moment of reflection.
 * This modal does -- before any send fires, the operator sees:
 *   * Campaign name + sender mailbox
 *   * Recipient count + 3 sample addresses (sanity check the audience)
 *   * Number of steps
 *   * Test-mode status, color-coded
 *   * A clear "Yes, send live emails" / "Yes, send test emails" CTA
 *
 * Skipped (fires the action immediately) when EITHER:
 *   * Recipient count is 0 -- nothing to send, no risk
 *   * Test mode is engaged AND recipient count <= 1 -- single-recipient
 *     test runs are common during iteration; don't add friction
 *
 * Resume action (status='paused' -> 'active') uses the same modal so
 * "I paused for an edit, now I'm restarting" is just as guarded as
 * the initial start.
 */
export function StartCampaignConfirm({
  campaign,
  onConfirm,
  onCancel,
}: {
  campaign: Campaign;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}) {
  const settingsQ = useOutreachSettings();
  const recipientsQ = useCampaignRecipients(campaign.id, undefined, 'campaign');
  const [submitting, setSubmitting] = useState(false);

  const globalTest = !!settingsQ.data?.test_mode_enabled;
  const isTestMode = globalTest || campaign.test_mode_enabled;
  const testRecipient = settingsQ.data?.test_recipient || 'brad+test@ember-infra.com';

  const recipients = recipientsQ.data;
  const total = recipients?.by_enrollment_kind?.campaign ?? 0;
  const rsvpOnlyTotal = recipients?.by_enrollment_kind?.rsvp_only ?? 0;
  const samples = (recipients?.rows || [])
    .slice(0, 3)
    .map((r) => r.email);

  const action = campaign.status === 'paused' ? 'Resume' : 'Start';

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 1000, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF',
          padding: 0,
          borderRadius: 8,
          minWidth: 520,
          maxWidth: 640,
          fontFamily: "'DM Sans', sans-serif",
          overflow: 'hidden',
          // Red border when about to send live emails -- visual loud-mode
          // signal that closes the "I forgot test mode was off" gap.
          border: isTestMode ? '2px solid #2E6E2E' : '3px solid #B04030',
        }}
      >
        {/* Header band */}
        <div
          style={{
            background: isTestMode ? '#E5F4E5' : '#FCEFEC',
            color: isTestMode ? '#1F4F1F' : '#7B2820',
            padding: '14px 20px',
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {isTestMode ? (
            <>⚙ {action} a TEST campaign</>
          ) : (
            <>⚠ {action} a LIVE campaign — emails will be sent to real recipients</>
          )}
        </div>

        <div style={{ padding: 20 }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Campaign" value={campaign.name} />
              <Row label="Sender" value={campaign.sender_email} mono />
              <Row
                label="Recipients"
                value={
                  recipientsQ.isLoading
                    ? 'loading…'
                    : total === 0
                    ? '0  (nothing to send — enroll first)'
                    : `${total} sendable`
                }
              />
              {rsvpOnlyTotal > 0 && (
                <Row
                  label="RSVP-only"
                  value={`${rsvpOnlyTotal} enrolled for forms / calendar queue`}
                />
              )}
              {samples.length > 0 && (
                <Row
                  label="Sample"
                  value={
                    samples.join(', ')
                    + (total > samples.length
                      ? ` (and ${total - samples.length} more)`
                      : '')
                  }
                  mono
                />
              )}
              <Row label="Steps" value={String(campaign.steps.length)} />
              <Row
                label="Test mode"
                value={
                  isTestMode ? (
                    <span style={{ color: '#1F4F1F', fontWeight: 600 }}>
                      ON{' '}
                      {globalTest && campaign.test_mode_enabled
                        ? '(global + per-campaign)'
                        : globalTest
                        ? '(global)'
                        : '(per-campaign)'}{' '}
                      → routes to {testRecipient}
                    </span>
                  ) : (
                    <span style={{ color: '#7B2820', fontWeight: 700 }}>
                      OFF — live emails will reach the addresses above
                    </span>
                  )
                }
              />
            </tbody>
          </table>

          {!isTestMode && (
            <div
              style={{
                marginTop: 16,
                padding: 10,
                background: '#FCEFEC',
                border: '1px solid #E5B5B0',
                borderRadius: 4,
                color: '#7B2820',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              This campaign is <strong>not</strong> in test mode. The first
              email will fire on the sequencer's next tick (within ~60s).
              The {campaign.min_seconds_between_sends_per_inbox || 67}-second
              throttle means there's a window of about{' '}
              {Math.ceil(
                (total * (campaign.min_seconds_between_sends_per_inbox || 67))
                  / 60,
              )}{' '}
              minute(s) before all recipients receive their copy — pause
              from the campaign view if you spot something wrong.
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex', gap: 8, justifyContent: 'flex-end',
            padding: 16, borderTop: '1px solid #E5E5E0',
            background: '#FAF9F4',
          }}
        >
          <button
            type="button"
            className={styles.button}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.button}
            style={
              isTestMode
                ? { background: '#2E6E2E', color: '#FFF', borderColor: '#2E6E2E' }
                : { background: '#B04030', color: '#FFF', borderColor: '#B04030' }
            }
            onClick={handleConfirm}
            disabled={submitting || total === 0}
            title={
              total === 0
                ? 'Enroll recipients before starting'
                : undefined
            }
          >
            {submitting
              ? `${action.toLowerCase().replace('e', 'i')}ng…`
              : isTestMode
              ? `Yes, ${action.toLowerCase()} test campaign`
              : `Yes, send live emails to ${total}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function Row({
  label, value, mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <tr>
      <td
        style={{
          width: 110, padding: '6px 12px 6px 0',
          color: '#888', fontSize: 12, verticalAlign: 'top',
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: '6px 0', verticalAlign: 'top',
          fontFamily: mono ? "'DM Mono', monospace" : 'inherit',
          fontSize: mono ? 12 : 13,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </td>
    </tr>
  );
}
